import React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { Grid } from '@mui/material';
import { TextField } from 'formik-mui';
import { Field, FormikProps, FormikProvider } from 'formik';
import ReactQuill from 'react-quill';
import * as Yup from 'yup';

import { ILayoutContext, LayoutContext, ModuleHandle, ToastSeverityEnum, useUderlyStore } from '@uderly/react-uderly-ui';
import Module, { ModuleHostProps } from "../components/Module";
import useMediaImage from '../components/Module/hooks/useMediaImage';
import AppContext from '../AppContext';
import IqSpaceApi from '../../api/IqSpace';
import getPlaylistColumns from '../components/getPlaylistColumns';
import Separator from '../components/Separator';
import PlaylistQuestions from '../components/PlaylistQuestions';
import { hasPermission } from '../utils';
import { IqStore } from '../../models/IqStore';
import { StoreBreadcrumbs } from "../components/Module/StoreBreadcrumbs";

interface PlaylistsProps extends ModuleHostProps {
  storeSubdomain: string;
}

const Playlists = (props: PlaylistsProps) => {
  const { t } = useTranslation();

  const layoutContext = useContext(LayoutContext) as ILayoutContext;

  const zustandStore = useUderlyStore(s => s.store);
  const user = useUderlyStore(s => s.user);

  const [store, setStore] = useState<IqStore | null>(zustandStore as IqStore);

  useEffect(() => {
    const init = async () => {
      const response = await IqSpaceApi.Store(props.storeSubdomain);

      if (response && response.status === 200) {
        setStore(response.data.data);
      }
    }

    init();
  }, [props.storeSubdomain]);

  const moduleData = {
    title: <StoreBreadcrumbs store={store} path="/playlists" />,
    name: t('playlists'),
    path: `/${store?.subdomain}/playlists`,
    apiBasePath: '/playlists'
  };

  const moduleRef = useRef<ModuleHandle>(null);

  const [item, setItem] = useState<any>(null);

  const mediaImage = useMediaImage(item, moduleRef, moduleData);

  const columns = getPlaylistColumns(moduleData.apiBasePath);

  const initialValues = {
    name: ""
  };

  const schema = () => {
    const v = {
      name: Yup.string().min(2).required((t('required-field') as string))
    };

    return Yup.object(v);
  }

  const onItemLoaded = (item: any) => {
    if (item && item.id) {
      setItem(item);
    }
  };

  const view = (_item: any, formik: FormikProps<any>) => {
    const { values, setFieldValue } = formik;

    if (!store) return null;

    return (
      <FormikProvider value={formik}>
        <Grid container>
          <Grid container item spacing={1} sm md lg xl>
            <Grid item xs={12} xl={12}>
              <Field component={TextField} name="name" type="text" readonly
                label={AppContext.r["name"] + "*"} />
            </Grid>

            {hasPermission(user, "content_management") &&
              <Grid item xs={12}>
                <h3>{t('description')}</h3>
                <ReactQuill value={values.description}
                  modules={{
                    clipboard: {
                      matchVisual: false
                    }
                  }}
                  onChange={(value) => setFieldValue('description', value)} />
              </Grid>}

            {values.created_at &&
              <Grid item xs={12} sm={4} xl={4}>
                <div className="outlined-with-label disabled">
                  {formatInTimeZone(values.created_at, values.timezone, "dd/MM/yyyy H:mm:ss")}
                  <label>{AppContext.r["created-at"]}</label>
                </div>
              </Grid>}
          </Grid>

          <Grid item xs={12} sm={3} padding={1} className='media-image-container'>
            {mediaImage.renderComponent(formik, { apiPath: "/playlists" })}
          </Grid>
        </Grid>

        <div className="questions-view">
          {(values && values.questions) &&
            <>
              <h3 className="text-center">{values.questions_count} {AppContext.r["questions"]}</h3>
              <Separator />

              <div className="top-menu"></div>

              <PlaylistQuestions playlist={values} 
                onQuestionsChanged={p => setFieldValue("questions_count", p.length)}
                onQuestionRemoved={(_q) => setFieldValue("questions_count", values.questions_count - 1)}
                onQuestionInserted={(_q) => setFieldValue("questions_count", values.questions_count + 1)} />
            </>}
        </div>
      </FormikProvider >
    );
  };

  const onDelete = (response) => {
    if (response && response.status === 406)
      layoutContext.toast.show({
        open: true,
        content: AppContext.r["cant-delete-default-playlist"],
        severity: ToastSeverityEnum.error,
      });
  }

  return (
    <Module
      ref={moduleRef}
      className='playlists'
      columns={columns}
      schema={schema}
      view={view}
      data={moduleData}
      initialValues={initialValues}
      initialFilters={{ store_id: store?.id, language_id: user?.language_id }}
      onItemLoaded={onItemLoaded}
      onDelete={onDelete}
    />
  );
}

export default Playlists;